// ============================================================================
// T-ALE-14 · LA FRANJA QUE SE VE (R-ALE-01) · T-ALE-15 · AHORA SOLO LOS GRAVES.
//
// Mientras haya un incidente abierto que le concierna a quien está mirando, el
// panel lo dice EN EL MARCO, arriba de todo y en todas las pantallas — no en
// una pantalla aparte a la que haya que entrar. Es la contrapartida obligatoria
// de R-MOT-08: al quitarse el tope, el bot puede seguir atendiendo
// indefinidamente con un entrenamiento viejo, y lo único que impide que eso
// pase inadvertido es que se VEA.
//
// ⭐ T-ALE-15: LA FRANJA SE QUEDA, PERO SOLO PARA LOS `grave`. Lo que estorba
//    tiene que ser lo que de verdad estorba; los `aviso` bajan a la campanita,
//    que los lleva todos con su «ya lo vi». Una franja que grita por todo se
//    vuelve papel tapiz, y entonces no grita por nada.
//
// ⭐ Y YA NO CONSULTA NADA: recibe el estado de `useAlertas`, que es el ÚNICO
//    origen y el mismo que consume la campanita. Si cada una consultara por su
//    lado, dar por visto en la campanita dejaría el cartel aquí hasta 60 s.
//
// Lo que esta pieza NO hace, y es a propósito:
//   ⛔ no filtra por inquilino en el navegador — quien reparte es la RLS;
//   ⛔ no pinta el `detalle` cuando quien mira no es Victorix: el motivo técnico
//      ni siquiera se le pidió a la base;
//   ⛔ no se queda callada si la consulta falla. Una franja vacía significa
//      «todo bien»; callarse ante un error sería un botón que miente.
// ============================================================================
import { ShieldAlert, WifiOff } from "lucide-react";
import type { EstadoAlertas } from "./useAlertas.ts";

/** «desde cuándo», en el idioma del dueño: nada de fechas ISO en una franja que
 *  se lee de reojo. Exportada para que la vara la mida sin montar el reloj. */
export function desdeHace(iso: string, ahora = new Date()): string {
  const ms = ahora.getTime() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "hace un momento";
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? "desde ayer" : `hace ${d} días`;
}

export function FranjaAlertas({ estado }: { estado: EstadoAlertas }) {
  const { alertas, incompleta, fallo, esVictorix } = estado;

  if (fallo) {
    return (
      <div role="status" aria-live="polite" data-franja="fallo"
        className="shrink-0 flex items-center gap-2 px-3 md:px-4 py-2 text-[13px] leading-snug
          border-b border-borde bg-tenue text-tinta-2">
        <WifiOff size={15} strokeWidth={1.75} className="shrink-0" aria-hidden="true" />
        <span>No pude comprobar el estado del bot. Vuelvo a intentarlo en un minuto.</span>
      </div>
    );
  }

  // ⭐ SOLO LOS GRAVES. Los avisos siguen visibles —y con su «ya lo vi»— en la
  //    campanita, que consume ESTA MISMA lista.
  const graves = (alertas ?? []).filter((a) => a.severidad === "grave");
  if (graves.length === 0) return null;

  return (
    <div role="status" aria-live="polite" data-franja="grave"
      className="shrink-0 border-b border-peligro/30 bg-peligro/10 text-peligro
        max-h-[132px] overflow-y-auto">
      <ul className="px-3 md:px-4 py-1.5 space-y-1">
        {graves.map((a) => (
          <li key={a.id} data-alerta={a.clave}
            className="flex items-start gap-2 text-[13px] leading-snug">
            <ShieldAlert size={15} strokeWidth={1.75} className="shrink-0 mt-[2px]" aria-hidden="true" />
            <span className="min-w-0">
              <span className="font-medium">{a.titulo}</span>
              <span className="opacity-70"> · {desdeHace(a.creada_en)}</span>
              {/* ⛔ SOLO VICTORIX. Para el dueño, `detalle` ni siquiera se pidió
                  a la base (ver `listarAlertasAbiertas`), así que este renglón
                  no existe: no es que se esconda, es que no llegó. */}
              {esVictorix && a.detalle && Object.keys(a.detalle).length > 0 && (
                <span data-detalle-tecnico className="block font-mono text-[11px] opacity-60 break-all">
                  {JSON.stringify(a.detalle)}
                </span>
              )}
            </span>
          </li>
        ))}
        {/* ⛔ Un corte que tapa CALLADO es el fallo que este carril persigue. La
            vista `v_alertas_abiertas` ya devuelve una fila por incidente, así
            que llegar al tope significa que de verdad hay más de los que caben
            — y entonces se dice, en vez de dar a entender que esos son todos. */}
        {incompleta && (
          <li data-franja-incompleta className="flex items-start gap-2 text-[13px] leading-snug">
            <ShieldAlert size={15} strokeWidth={1.75} className="shrink-0 mt-[2px]" aria-hidden="true" />
            <span className="font-medium">
              Hay más incidentes abiertos de los que caben aquí: estos no son todos.
            </span>
          </li>
        )}
      </ul>
    </div>
  );
}
